import EventEmitter from 'events';

// Constants
const LOG_20 = Math.log(20);

// Tunables
const rttAlpha = 0.2;
const jitterAlpha = 1 / 16; // RFC-style
const scoreAlpha = 0.2;
const rttTimeoutMs = 3000;

// Helper functions
const ewma = (prev, value, alpha) => (alpha * value) + ((1 - alpha) * prev);

const clamp = v => Math.max(0, Math.min(v, 1));

const createSignalingNetworkQualityEstimator = () => {
  const ee = new EventEmitter();

  // State
  let initialized = false;
  let smoothedScore = 0.8;
  let score = 4;
  let avgRttMs = 0;
  let lastRttMs = 0;
  let jitterMs = 0;
  let missedAcks = [];
  let missedAcksTimeout;
  let receivedAcksTimeout;

  const rttScore = () => {
    // ~50ms good, ~1s bad
    const logRatio = Math.log(avgRttMs / 50) / LOG_20;
    return clamp(1 - logRatio);
  };

  const jitterScore = () => clamp(1 - (jitterMs / 100));

  const reliabilityScore = () => clamp(1 - (missedAcks.length / 5));

  /**
   * In order to compute the score, we take into account RTT, jitter and reliability (missed ACKs).
   * The score is smoothed using an EWMA to avoid drastic changes. The score is an integer between
   * 1 and 5, where 5 is the best quality. The weights for RTT, jitter and reliability are 0.4, 0.25
   * and 0.35 respectively, based on subjective evaluation of their impact on the user experience.
   * If we haven't received any ACKs (what may happen when the sender-side stats feature is not enabled),
   * we only use the reliability score to avoid penalizing the score due to lack of data.
   */
  const computeScore = () => {
    const newScore = initialized ?
      clamp((0.4 * rttScore()) + (0.25 * jitterScore()) + (0.35 * reliabilityScore())) :
      reliabilityScore();
    smoothedScore = ewma(smoothedScore, newScore, scoreAlpha);
    const prevScore = score;
    // smoothedScore is [0,  1], and we want to return integer [1, 5]
    score = Math.round(smoothedScore * 4) + 1;
    if (prevScore !== score) {
      ee.emit('signalingNetworkQualityScoreChanged', score);
    }
  };

  const getScore = () => score;

  const clearReceivedAcks = () => {
    initialized = false;
    avgRttMs = 0;
    lastRttMs = 0;
    jitterMs = 0;
    receivedAcksTimeout = null;
    computeScore();
  };

  const addReceivedAck = (sentAt) => {
    if (receivedAcksTimeout) {
      clearTimeout(receivedAcksTimeout);
    }
    receivedAcksTimeout = setTimeout(clearReceivedAcks, 10000);
    const now = Date.now();
    let rtt = now - sentAt;

    // Timeout → reliability degradation
    if (rtt > rttTimeoutMs) {
      rtt = rttTimeoutMs;
    }

    if (!initialized) {
      avgRttMs = rtt;
      lastRttMs = rtt;
      jitterMs = 0;
      initialized = true;
    } else {
      // Proper jitter (RTT variation)
      const delta = Math.abs(rtt - lastRttMs);
      jitterMs = ewma(jitterMs, delta, jitterAlpha);

      // RTT smoothing
      avgRttMs = ewma(avgRttMs, rtt, rttAlpha);
      lastRttMs = rtt;
    }

    computeScore();
  };

  const clearMissedAcks = () => {
    const now = Date.now();
    missedAcks = missedAcks.filter(timestamp => now - timestamp < 60000);
    if (missedAcks.length > 0) {
      const time = Math.max(0, 60000 - (now - missedAcks[0]));
      missedAcksTimeout = setTimeout(clearMissedAcks, time);
    } else {
      missedAcksTimeout = null;
    }
    computeScore();
  };

  const addMissedAck = () => {
    const now = Date.now();
    if (missedAcks.length === 5) {
      missedAcks.shift(); // remove oldest to keep it to 5 maximum
      clearTimeout(missedAcksTimeout);
      missedAcksTimeout = null;
    }
    missedAcks.push(now);
    if (!missedAcksTimeout) {
      const time = Math.max(0, 60000 - (now - missedAcks[0]));
      missedAcksTimeout = setTimeout(clearMissedAcks, time);
    }
    computeScore();
  };

  return Object.assign(ee, {
    getScore,
    addReceivedAck,
    addMissedAck,
  });
};

export default createSignalingNetworkQualityEstimator;
